import { useAsync, useSharedState, useUrlParams } from "../../../../hooks"
import { LocationService } from "../../../../services"
import { ErrorPage } from "../../../error";
import { BookingSummary, ServiceSelection } from "../../components";
import { BookingModel } from "../../../../data/entities/booking/BookingModel";
import { Row, Col, Card, Modal, Button as BSButton, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useServicesByType } from "../../hooks/useServicesByType";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookingWithDetails } from "../../../../models/booking/BookingWithDetails";
import BookingService from "../../../../services/booking/BookingService";
import { router } from "../../../../app/routes";
import { clearBookingStorage, loadCustomerBookingInfo, trackDraftEvent } from "../../../../utils";

export function BookingPage() {
  const locationCode = (useUrlParams("locationCode"))
  const locationService = new LocationService();
  const locationRouter = useLocation();
  const bookingService = new BookingService();

  const restoreBooking = locationRouter.state?.restoreBooking as BookingWithDetails | undefined;
  const restoreAnyProviderFlags = (locationRouter.state?.anyProviderFlags ?? []) as boolean[];
  const hasRestoredRef = useRef(false);
  const hasSeededPrefsRef = useRef(false);

  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [showMobileSummary, setShowMobileSummary] = useState(false);

  const {value: location, error, loading } = useAsync(() => locationService.getWithAddressAndRegion(locationCode), [locationCode])
  const locationId = location?.id ?? 0;
  const {value: locationServices} = useAsync( () => location ? locationService.getProviderServices(locationId) : Promise.resolve([]),
    [location]
  )
  const servicesByType = useServicesByType(locationServices);

  const booking = useSharedState<BookingModel>({
    id: 0,
    tenantId: 0,
    locationId: locationId,
    customerId: 0,
    isCompleted: false,
    sendConfirmationToPhone: false,
    bookingEvents: []
  } as BookingModel);

  const eventCount = booking.value.bookingEvents?.length ?? 0;
  const prevCountRef = useRef(eventCount);

  // Pulse the bottom bar when a service is added
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (eventCount > prevCountRef.current) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(timer);
    }
    prevCountRef.current = eventCount;
  }, [eventCount]);

  //Removing all services closes the mobile offcanvas
  useEffect(() => {
    if (eventCount === 0) {
      setShowMobileSummary(false);
    }
  }, [eventCount]);


  // Check for existing draft on mount
  useEffect(() => {
    const checkDraft = async () => {
      const activeBookingId = localStorage.getItem("activeBookingId");

      if (restoreBooking) {
        return;
      }

      if (activeBookingId) {
        try {
          const status = await bookingService.getDraftStatus(parseInt(activeBookingId));

          if (status?.status === "active") {
            setShowDraftPrompt(true);
          } else {
            clearBookingStorage();
          }
        } catch {
          clearBookingStorage();
        }
      }
    };

    checkDraft();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueDraft = () => {
    setShowDraftPrompt(false);
    trackDraftEvent("resume_draft_booking", Number(localStorage.getItem("activeBookingId")) || null);
    router.navigate(`/invoice/${locationCode}`);
  };

  const handleStartOver = async () => {
    const activeBookingId = localStorage.getItem("activeBookingId");
    trackDraftEvent("discard_draft_booking", Number(activeBookingId) || null);
    if (activeBookingId) {
      try {
        const invoiceId = localStorage.getItem("activeBookingInvoiceId");
        await bookingService.discardDraftBookingAsync(
          parseInt(activeBookingId),
          invoiceId ? parseInt(invoiceId) : 0
        );
      } catch {
      }
      clearBookingStorage();
    }
    setShowDraftPrompt(false);
  };

  useEffect(() => {
    if (locationId && booking.value.locationId !== locationId ) {
      booking.setter({
        ...booking.value,
        locationId,
      });
    }
  }, [booking, locationId]);

  // Restore booking state when coming back from invoice page
  useEffect(() => {
    if (restoreBooking && locationId && !hasRestoredRef.current) {
      hasRestoredRef.current = true;

      const bookingDate = restoreBooking.date || restoreBooking.bookingEvents?.[0]?.date;

      const restoredBookingModel: BookingModel = {
        id: 0,
        tenantId: restoreBooking.tenantId || 0,
        locationId: locationId,
        customerId: restoreBooking.customer?.id || 0,
        isCompleted: false,
        sendConfirmationToPhone: restoreBooking.sendConfirmationToPhone || false,
        date: bookingDate,
        customer: restoreBooking.customer,
        bookingEvents: (() => {
          const anyProviderFlags: boolean[] = restoreAnyProviderFlags;
          return restoreBooking.bookingEvents?.map((event, index) => {
            const isAnyProvider = anyProviderFlags[index] ?? false;
            return {
              id: event.id || 0,
              bookingId: 0,
              serviceId: event.service?.id || event.serviceId || 0,
              providerId: isAnyProvider ? null : (event.provider?.id || event.providerId || null),
              isAnyProvider,
              date: bookingDate || event.date,
              startTime: event.startTime,
              endTime: event.endTime,
              duration: event.duration || event.service?.defaultDuration || 0,
              service: event.service,
              provider: isAnyProvider ? undefined : event.provider,
            };
          }) || [];
        })()
      };

      booking.setter(restoredBookingModel);
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreBooking, restoreAnyProviderFlags, locationId]);

  useEffect(() => {
    if (restoreBooking || hasSeededPrefsRef.current) return;
    hasSeededPrefsRef.current = true;

    const savedCustomer = loadCustomerBookingInfo();
    if (!savedCustomer) return;

    booking.setter({
      ...booking.value,
      customer: {
        ...booking.value.customer,
        firstName: savedCustomer.firstName,
        lastName: savedCustomer.lastName,
        email: savedCustomer.email,
        phone: savedCustomer.phone,
      },
      sendConfirmationToPhone:
        savedCustomer.sendConfirmationToPhone ?? booking.value.sendConfirmationToPhone,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreBooking]);

  useEffect(() => {
    return () => {
      hasRestoredRef.current = false;
      hasSeededPrefsRef.current = false;
    };
  }, [locationCode]);

  if(error) return <ErrorPage message={error.message} />
  if(loading || locationId === 0) return <></>

  return (
    <motion.div
      initial={{ y: "10px", opacity: 0 }}
      animate={{y: 0, opacity: 1 }}
      exit={{ y: "50%", opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut"}}
    >
      {/* Location Header */}
      <Card className="mb-4 shadow-sm border-0" style={{ overflow: 'hidden', backgroundColor: 'white' }}>
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, #454646 0%, #2f4157 100%)'
        }}></div>

        <Card.Body className="p-3 p-md-4">
          <div className="text-center">
            <h1 className="mb-1 fs-4 fs-md-3" style={{ color: '#2f4157', fontWeight: '600' }}>
              {location!.name}
            </h1>

            <div className="d-flex justify-content-center align-items-center mb-2">
              <i className="fa fa-map-marker me-2"></i>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${location!.address?.streetAddress}, ${location!.address?.city}, ${location!.address?.region?.abbreviation}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fw-medium small"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {location!.address?.streetAddress}, {`${location!.address?.city}, ${location!.address?.region?.abbreviation}`}
              </a>
            </div>

            <div className="pt-2 mt-1 d-inline-block px-4"
                 style={{ borderTop: '1px solid rgba(47, 65, 87, 0.2)' }}>
              <p className="mb-0 small" style={{ color: '#2f4157' }}>
                <i className="fa fa-info-circle me-2"></i>
                Select services below to build your appointment
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* No Services Message */}
      {Object.keys(servicesByType).length === 0 && (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-5 text-center">
            <i className="fa fa-calendar-times-o fa-3x mb-3" style={{ color: '#577c8e' }}></i>
            <h3 style={{ color: '#2f4157' }}>No Services Available</h3>
            <p className="mb-0" style={{ color: '#577c8e' }}>
              There are currently no services available at this location.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Services and Booking Summary */}
      <Row className="mt-3 mt-md-4" style={{ paddingBottom: eventCount > 0 ? '85px' : '0' }}>
        {/* Desktop Sidebar Summary (hidden on mobile) */}
        {eventCount > 0 && (
          <Col lg={10} className="d-none d-lg-block mt-2 mt-md-0 order-lg-2">
            <div className="position-sticky" style={{ top: '16px' }}>
              <BookingSummary booking={booking} locationId={locationId} locationCode={locationCode} />
            </div>
          </Col>
        )}

        <Col
          xs={24}
          lg={eventCount > 0 ? 14 : 24}
          className={`order-lg-1${eventCount > 0 ? ' mt-lg-0' : ''}`}
        >
          {Object.entries(servicesByType).map(([typeName, services]) => (
            <div key={typeName} className="mb-4">
              <div className="mb-3 pb-2" style={{ borderBottom: '1px solid #c7d9e5' }}>
                <div className="d-flex align-items-center">
                  <div style={{
                    height: '24px',
                    width: '4px',
                    backgroundColor: '#577c8e',
                    borderRadius: '2px',
                    marginRight: '12px'
                  }}></div>
                  <h4 className="mb-0 fs-5" style={{
                    color: '#2f4157',
                    fontWeight: '600',
                    letterSpacing: '-0.02em'
                  }}>
                    {typeName}
                  </h4>
                </div>
              </div>
              <ServiceSelection 
                services={services} 
                booking={booking}
                locationId={locationId} 
              />
            </div>
          ))}
        </Col>
      </Row>

      {/* Mobile Sticky Bottom Bar */}
      <AnimatePresence>
        {eventCount > 0 && (
          <motion.div
            className="d-lg-none position-fixed bottom-0 start-0 end-0"
            initial={{ y: '100%' }}
            animate={{
              y: 0,
              scale: pulse ? [1, 1.02, 1] : 1,
            }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            style={{
              zIndex: 1040,
              backgroundColor: '#2f4157',
              borderTop: '3px solid rgba(255,255,255,0.15)',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            }}
          >
            {/* Tap to expand */}
            <div
              className="d-flex justify-content-between align-items-center px-3 py-2"
              onClick={() => setShowMobileSummary(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex align-items-center text-white">
                <div
                  className="d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    fontSize: '0.85rem',
                    fontWeight: '700',
                  }}
                >
                  {eventCount}
                </div>
                <div>
                  <span className="fw-semibold" style={{ fontSize: '0.95rem' }}>
                    {eventCount === 1 ? '1 service' : `${eventCount} services`} selected
                  </span>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    Tap to review
                  </div>
                </div>
              </div>

              <i className="fa fa-chevron-up text-white" style={{ opacity: 0.8 }}></i>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Slide-Up Summary Drawer */}
      <Offcanvas
        show={showMobileSummary}
        onHide={() => setShowMobileSummary(false)}
        placement="bottom"
        className="d-lg-none"
        style={{
          height: '85vh',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Offcanvas.Header
          closeButton
          className="pb-2"
          style={{ borderBottom: '1px solid #e9ecef', flexShrink: 0 }}
        >
          <Offcanvas.Title style={{ color: '#2f4157', fontWeight: '600' }}>
            Your Appointment
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body
          className="pt-3 pb-0 px-0"
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
          }}
        >
          <BookingSummary
            booking={booking}
            locationId={locationId}
            locationCode={locationCode}
            compact
          />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Draft Booking Prompt */}
      <Modal show={showDraftPrompt} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Booking In Progress</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">You have an unfinished booking. Would you like to continue where you left off or start a new booking?</p>
        </Modal.Body>
        <Modal.Footer>
          <BSButton variant="outline-secondary" onClick={handleStartOver}>
            Start Over
          </BSButton>
          <BSButton variant="primary" onClick={handleContinueDraft}>
            Continue Booking
          </BSButton>
        </Modal.Footer>
      </Modal>
    </motion.div>
  )
}

export default BookingPage